import { h as __h } from "preact";
import { ApiError, authedApiRequest } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import Query from "~/util/query.js";
import { confirmModal } from "~/component/ConfirmModal.js";
import { renameMachineModal } from "~/component/RenameMachineModal.js";

/** @import { hostResponse, machineAccessInviteResponse } from "~/backend/validate.js"; */
/** @import { DropdownItem } from "~/component/Dropdown.js"; */

/**
 * @param {hostResponse} host
 * @param {() => void} onShare
 * @returns {DropdownItem[]}
 */
export function machineActions(host, onShare) {
  const userId = identity.me?.id;
  if (!userId || identity.enterprise) return [];
  if (host.owner_id === userId) {
    return [{
      icon: "pencil",
      text: "Rename",
      onSelect: () => renameMachine(host)
    }, {
      icon: "users",
      text: "Share",
      onSelect: onShare
    }, {
      icon: "trash",
      text: "Delete",
      kind: "negative",
      disabled: host.is_online,
      tooltip: host.is_online ? "Computers can only be deleted while they're offline." : undefined,
      onSelect: () => deleteMachine(host)
    }];
  }
  return [{
    icon: "trash",
    text: "Remove",
    kind: "negative",
    onSelect: () => removeAccess(host)
  }];
}

/** @param {machineAccessInviteResponse} invite */
export async function acceptInvite(invite) {
  try {
    await authedApiRequest("POST", `/v2/access-invites/${invite.id}/accept`);
  } catch {
    alert(`Couldn't accept the invite to ${invite.machine.name}.`, {
      type: "error"
    });
  }
  Query.invalidate(["/v2/access-invites"], ["/v2/hosts"]);
}

/** @param {machineAccessInviteResponse} invite */
export async function declineInvite(invite) {
  try {
    await authedApiRequest("DELETE", `/v2/access-invites/${invite.id}`);
  } catch {
    alert(`Couldn't decline the invite to ${invite.machine.name}.`, {
      type: "error"
    });
  }
  Query.invalidate(["/v2/access-invites"]);
}

/** @param {hostResponse} host */
async function renameMachine(host) {
  if (!(await renameMachineModal(host))) return;
  Query.invalidate(["/v2/hosts"]);
  if (host.id === identity.machine?.id) identity.fetch();
}

/** @param {hostResponse} host */
async function deleteMachine(host) {
  const confirmed = await confirmModal({
    title: `Delete ${host.name}?`,
    action: "Delete",
    body: __h("p", {}, "You'll be able to add ", host.name, " again by opening Phaze on that computer.")
  });
  if (!confirmed) return;
  try {
    await authedApiRequest("DELETE", `/v2/machines/${host.id}`);
    alert(`Deleted ${host.name}`, {
      type: "success"
    });
  } catch (err) {
    alert(DeleteMachineError.from(err).message, {
      type: "error"
    });
  } finally {
    Query.invalidate(["/v2/hosts"]);
  }
}

/** @param {hostResponse} host */
async function removeAccess(host) {
  const confirmed = await confirmModal({
    title: `Remove ${host.name}?`,
    action: "Remove",
    body: __h("p", {}, "You won't be able to connect to ", host.name, " unless it's shared with you again.")
  });
  if (!confirmed) return;
  try {
    await authedApiRequest("DELETE", `/v2/machines/${host.id}/accesses/${identity.me?.id}`);
    alert(`Removed ${host.name}`, {
      type: "success"
    });
  } catch (err) {
    alert(RemoveAccessError.from(err).message, {
      type: "error"
    });
  } finally {
    Query.invalidate(["/v2/hosts"]);
  }
}
class DeleteMachineError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        not_found: "Couldn't find this computer. It may have already been deleted."
      }
    };
  }
}
class RemoveAccessError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        not_found: "Couldn't find this computer. It may have already been removed."
      }
    };
  }
}